'use client';

import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Languages } from 'lucide-react';
import { Locale } from '@/config/routes';
import { getLocaleFlagUrl, getLocaleDisplayName } from '@/lib/locale';

type Translation = {
  bs: string | null;
  en: string | null;
  tr: string | null;
  currentLocale: string;
};

type Props = {
  translations: Translation;
};

export function BlogPostLanguageSwitcher({ translations }: Props) {
  const router = useRouter();

  const availableTranslations = [
    { locale: 'bs' as Locale, slug: translations.bs },
    { locale: 'en' as Locale, slug: translations.en },
    { locale: 'tr' as Locale, slug: translations.tr },
  ].filter((t) => t.slug !== null || t.locale === translations.currentLocale);

  // If only one language is available (current one), don't show the switcher
  if (availableTranslations.length <= 1) {
    return null;
  }

  const handleLanguageSwitch = (locale: Locale, slug: string | null) => {
    if (!slug) return; // Current language, do nothing

    const url = locale === 'bs' ? `/blog/${slug}` : `/${locale}/blog/${slug}`;
    router.push(url);
  };

  return (
    <div className="flex items-center gap-3 mb-8">
      <div className="flex items-center gap-2 text-sm text-foreground/60">
        <Languages className="w-4 h-4" />
        <span>Also available in:</span>
      </div>
      <div className="flex gap-2">
        {availableTranslations.map((t) => {
          const isCurrent = t.locale === translations.currentLocale;
          const isAvailable = t.slug !== null;

          if (isCurrent) {
            return (
              <span
                key={t.locale}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium"
              >
                <Image
                  src={getLocaleFlagUrl(t.locale)}
                  alt={getLocaleDisplayName(t.locale)}
                  width={20}
                  height={15}
                  className="rounded-sm"
                  unoptimized
                />
                {getLocaleDisplayName(t.locale)}
              </span>
            );
          }

          if (!isAvailable) {
            return null;
          }

          return (
            <button
              key={t.locale}
              onClick={() => handleLanguageSwitch(t.locale, t.slug)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg glass hover:bg-white/40 text-sm font-medium transition-colors"
            >
              <Image
                src={getLocaleFlagUrl(t.locale)}
                alt={getLocaleDisplayName(t.locale)}
                width={20}
                height={15}
                className="rounded-sm"
                unoptimized
              />
              {getLocaleDisplayName(t.locale)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
